import { useSyncExternalStore } from "react";

/**
 * Tells an open tab that a newer build is deployed.
 *
 * The app is one page. A tab opened before a deploy keeps running the old code until a full reload,
 * and a browser can bring back an old page from its back-forward cache. So the app compares the
 * build id in its own bundle with the one in `/version.json`, which the server never caches. It
 * checks when the tab comes back into view, when the window gets focus, and every five minutes.
 *
 * When the ids differ, `isStale()` turns true. The root layout then shows a bar with a Reload
 * button, and the next change of page does a full load instead of a client-side one, so the user
 * gets the new build without being interrupted in the middle of a form.
 */

/**
 * Set by `define` in the app's vite.config.ts (apps/desk-web). In tests and the dev server it is not
 * defined and falls back to "dev".
 */
declare const TEAMNA_BUILD_ID: string | undefined;

/**
 * Where the app is mounted, without a trailing slash. Both Desk (desk.teamna.app) and Control Center
 * (cc.teamna.app) are at the root, so it is empty unless an app mounts under a prefix.
 */
let basePath = "";

export function setVersionCheckBase(base: string): void {
  basePath = base.replace(/\/+$/, "");
}

export const CURRENT_BUILD = typeof TEAMNA_BUILD_ID === "string" ? TEAMNA_BUILD_ID : "dev";
const CHECK_EVERY_MS = 5 * 60 * 1000;

let stale = false;
const listeners = new Set<() => void>();

function setStale(): void {
  if (stale) return;
  stale = true;
  for (const listener of listeners) listener();
}

/**
 * The browser address for a route inside the app. The router gives the path without the mount
 * prefix; a full load needs it back when there is one, or the browser leaves the app for a 404.
 */
export function fullAppHref(routeHref: string): string {
  if (basePath === "") return routeHref.startsWith("/") ? routeHref : `/${routeHref}`;
  if (routeHref === basePath || routeHref.startsWith(`${basePath}/`)) return routeHref;
  return `${basePath}${routeHref.startsWith("/") ? "" : "/"}${routeHref}`;
}

export function isStale(): boolean {
  return stale;
}

/** Pure, for tests: a deployed id that is present and different means this tab is old. */
export function isNewerBuild(current: string, deployed: string | null | undefined): boolean {
  if (!deployed || current === "dev") return false;
  return deployed !== current;
}

export async function checkForNewBuild(fetcher: typeof fetch = fetch): Promise<boolean> {
  try {
    const response = await fetcher(`${basePath}/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) return false;
    const body = (await response.json()) as { version?: string };
    if (isNewerBuild(CURRENT_BUILD, body.version)) {
      setStale();
      return true;
    }
  } catch {
    // Offline or mid-deploy. Try again at the next check.
  }
  return false;
}

/** Starts the checks. Returns a function that stops them. */
export function startVersionChecks(): () => void {
  if (CURRENT_BUILD === "dev") return () => {};
  const run = () => void checkForNewBuild();
  const onVisible = () => {
    if (document.visibilityState === "visible") run();
  };
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", run);
  // A page restored from the back-forward cache runs no script on its own, so check at once.
  window.addEventListener("pageshow", run);
  const timer = window.setInterval(run, CHECK_EVERY_MS);
  run();
  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", run);
    window.removeEventListener("pageshow", run);
    window.clearInterval(timer);
  };
}

/**
 * A code chunk from the old build is gone from the server after a deploy. Vite reports that as
 * `vite:preloadError`. Reload once to get the new build; the session flag stops a reload loop if
 * the server itself is broken.
 */
export function reloadOnStaleChunk(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const key = "teamna-chunk-reload";
    try {
      if (sessionStorage.getItem(key) === CURRENT_BUILD) return;
      sessionStorage.setItem(key, CURRENT_BUILD);
    } catch {
      // Storage blocked: reload anyway, one time is still better than a blank page.
    }
    event.preventDefault();
    window.location.reload();
  });
}

export function useIsStale(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    isStale,
    isStale,
  );
}
